import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, TextField, InputAdornment,
  Fab, Chip
} from '@mui/material';
import { Search, Add, WorkOff, PlaylistAddCheck, ErrorOutline, Assignment } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/axios';
import JobCard from '../shared/JobCard';
import LoadingState from '../shared/LoadingState';
import EmptyState from '../shared/EmptyState';

const JobsScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [search, setSearch] = useState('');

  const tabs = [
    { label: 'Needs Attention', filter: 'attention', icon: <ErrorOutline fontSize="small" />, path: '/jobs/attention' },
    { label: 'Job Pool', filter: 'unscheduled', icon: <Assignment fontSize="small" />, path: '/jobs/pool' },
    { label: 'Scheduled', filter: 'scheduled', icon: <PlaylistAddCheck fontSize="small" />, path: '/jobs/scheduled' },
    { label: 'Completed', filter: 'completed', icon: <PlaylistAddCheck fontSize="small" />, path: '/jobs/completed' },
    { label: 'Failed', filter: 'failed', icon: <ErrorOutline fontSize="small" />, path: '/jobs/failed' },
    { label: 'Issues', filter: 'issues', icon: <ErrorOutline fontSize="small" />, path: '/jobs/issues' }
  ];

  // Initialize tab based on URL path
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/pool')) setTab(1);
    else if (path.includes('/scheduled')) setTab(2);
    else if (path.includes('/completed')) setTab(3);
    else if (path.includes('/failed')) setTab(4);
    else if (path.includes('/issues')) setTab(5);
    else if (path.includes('/attention')) setTab(0);
    else setTab(0); // Default to Needs Attention if just /jobs
  }, [location.pathname]);

  useEffect(() => {
    loadJobs();
  }, [tab]);

  const handleTabChange = (e, newValue) => {
    setTab(newValue);
    navigate(tabs[newValue].path);
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const filter = tabs[tab].filter;
      let endpoint = '/admin/jobs';
      
      if (filter === 'attention') {
        const response = await api.get('/admin/jobs/needs-attention');
        // Flatten the response categories into a single list for display
        const data = response.data || {};
        const attentionJobs = [
          ...(data.needs_info || []).map(j => ({ ...j, tags: ['Needs Info'] })),
          ...(data.waiting_on_client || []).map(j => ({ ...j, tags: ['Waiting'] })),
          ...(data.incomplete_info || []).map(j => ({ ...j, tags: ['Incomplete'] }))
        ];
        // Remove duplicates by ID
        const uniqueJobs = Array.from(new Map(attentionJobs.map(item => [item.id, item])).values());
        setJobs(uniqueJobs);
      } else if (filter === 'unscheduled') {
        endpoint = '/admin/jobs/unscheduled';
        const response = await api.get(endpoint).catch(() => ({ data: [] }));
        setJobs(response.data || []);
      } else if (filter === 'completed') {
        endpoint = '/admin/jobs?status=Completed';
        const response = await api.get(endpoint).catch(() => ({ data: [] }));
        setJobs(response.data || []);
      } else if (filter === 'failed') {
        endpoint = '/admin/jobs?status=Failed';
        const response = await api.get(endpoint).catch(() => ({ data: [] }));
        setJobs(response.data || []);
      } else if (filter === 'issues') {
        // Unresolved Failed/Rescheduled — same queue as the planner's Issues tab
        endpoint = '/map/issues';
        const response = await api.get(endpoint).catch(() => ({ data: [] }));
        setJobs(response.data || []);
      } else if (filter === 'scheduled') {
        endpoint = '/admin/jobs?scheduled=true';
        const response = await api.get(endpoint).catch(() => ({ data: [] }));
        setJobs(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setJobs([]);
    }
    setLoading(false);
  };

  const filteredJobs = jobs.filter(job => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      job.description?.toLowerCase().includes(searchLower) ||
      job.location?.toLowerCase().includes(searchLower) ||
      job.id?.toString().includes(searchLower)
    );
  });

  const getEmptyStateProps = () => {
    if (search) {
      return {
        icon: <WorkOff />,
        title: 'No jobs found',
        description: `No jobs found matching "${search}"`,
        actionLabel: 'Clear Search',
        onAction: () => setSearch('')
      };
    }

    switch (tabs[tab].filter) {
      case 'attention':
        return {
          icon: <CheckCircle />,
          title: 'Nothing needs attention right now',
          description: 'All jobs are on track. Good job!',
        };
      case 'unscheduled':
        return {
          icon: <Assignment />,
          title: 'No unscheduled jobs — nice work!',
          description: 'Great work! The job pool is empty.',
          actionLabel: 'Create New Job',
          onAction: () => navigate('/jobs/new')
        };
      case 'scheduled':
        return {
            icon: <PlaylistAddCheck />,
            title: 'No scheduled jobs',
            description: 'Jobs will appear here once they are assigned a date and time.',
            actionLabel: 'Go to Job Pool',
            onAction: () => setTab(1)
        };
      case 'completed':
        return {
          icon: <PlaylistAddCheck />,
          title: 'No completed jobs yet',
          description: 'Completed jobs will be archived here.',
        };
      case 'failed':
        return {
          icon: <ErrorOutline />,
          title: 'No failed jobs',
          description: 'No issues reported. Smooth sailing!',
        };
      case 'issues':
        return {
          icon: <CheckCircle />,
          title: 'No unresolved issues',
          description: 'Failed or rescheduled jobs awaiting action appear here.',
        };
      default:
        return {
          icon: <WorkOff />,
          title: 'No jobs found',
          description: 'Get started by creating a new job.',
          actionLabel: 'Create New Job',
          onAction: () => navigate('/jobs/new')
        };
    }
  };

  return (
    <Box sx={{ pb: 10 }}>
      {/* Search */}
      <Paper 
        elevation={0} 
        sx={{ 
            p: 2, 
            mb: 3, 
            border: '1px solid #E2E8F0', 
            borderRadius: 3,
            bgcolor: 'white'
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search jobs by description, address or ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
            sx: { borderRadius: 2 }
          }}
          sx={{ 
            '& .MuiOutlinedInput-root': {
                bgcolor: '#F8FAFC',
                '& fieldset': { borderColor: '#E2E8F0' },
                '&:hover fieldset': { borderColor: '#CBD5E1' }
            }
          }}
        />
      </Paper>

      {/* Tabs */}
      <Paper 
        elevation={0} 
        sx={{ 
            mb: 3, 
            borderBottom: '1px solid #E2E8F0', 
            borderRadius: 0, 
            bgcolor: 'transparent' 
        }}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            '& .MuiTab-root': { 
                fontWeight: 600, 
                textTransform: 'none',
                minHeight: 48,
                fontSize: '0.95rem'
            }
          }}
        >
          {tabs.map((t, idx) => (
            <Tab key={idx} label={t.label} iconPosition="start" />
          ))}
        </Tabs>
      </Paper>

      {/* Jobs List */}
      <Box>
        {loading ? (
          <LoadingState message="Loading jobs..." />
        ) : filteredJobs.length === 0 ? (
          <Paper 
            elevation={0}
            sx={{ 
                borderRadius: 3,
                bgcolor: '#F8FAFC',
                border: '1px dashed #E2E8F0',
                overflow: 'hidden'
            }}
          >
            <EmptyState {...getEmptyStateProps()} />
          </Paper>
        ) : (
          <Box>
            {(() => {
              // Completed tab → done-work register: sort by completion time
              // (newest first), group by day, and tag invoice state so
              // unbilled work is visible without a spreadsheet.
              const isCompletedTab = tabs[tab].filter === 'completed';
              const sorted = isCompletedTab
                ? [...filteredJobs].sort((a, b) =>
                    String(b.completed_at || '').localeCompare(String(a.completed_at || '')))
                : filteredJobs;
              let lastDay = null;
              return sorted.map((job) => {
                const day = isCompletedTab && job.completed_at
                  ? String(job.completed_at).split('T')[0] : null;
                const header = isCompletedTab && day !== lastDay ? day : null;
                if (isCompletedTab) lastDay = day;
                return (
                  <Box key={job.id}>
                    {header && (
                      <Typography variant="subtitle2" fontWeight={700}
                        color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                        {new Date(day + 'T00:00:00').toLocaleDateString('en-AU',
                          { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                    )}
                    {isCompletedTab && (
                      <Chip
                        label={job.xero_invoice_id ? `Invoice ${job.xero_invoice_id}` : 'Not invoiced'}
                        size="small"
                        color={job.xero_invoice_id ? 'success' : 'default'}
                        variant={job.xero_invoice_id ? 'filled' : 'outlined'}
                        sx={{ mb: 0.5, fontWeight: 600 }}
                      />
                    )}
                    <JobCard
                      job={job}
                      onClick={(j) => navigate(`/jobs/${j.id}`)}
                      onDeleted={(deletedId) => {
                        setJobs(prev => prev.filter(j => j.id !== deletedId));
                      }}
                    />
                  </Box>
                );
              });
            })()}
          </Box>
        )}
      </Box>

      {/* FAB for new job */}
      <Fab
        color="primary"
        sx={{ 
            position: 'fixed', 
            bottom: 80, 
            right: 24,
            boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.4)'
        }}
        onClick={() => navigate('/jobs/new')}
      >
        <Add />
      </Fab>
    </Box>
  );
};

export default JobsScreen;
